import { isEqual, startOfWeek } from 'date-fns'
import { v4 as uuidv4 } from 'uuid'
import { addContentToList, reorderContentInList } from './contentManager'
import { buildDestinationMoment, buildOriginMoment } from './helpers'

export const actionTypes = {
  ACTIVATE_MODAL: 'ACTIVATE_MODAL',
  BOOTSTRAP_WEEK_VIEW: 'BOOTSTRAP_WEEK_VIEW',
  CHANGE_WEEK: 'CHANGE_WEEK',
  DEACTIVATE_MODAL: 'DEACTIVATE_MODAL',
  DELETE_CONTENT: 'DELETE_CONTENT',
  DELETE_MOMENT: 'DELETE_MOMENT',
  DUPLICATE_CONTENT: 'DUPLICATE_CONTENT',
  DUPLICATE_CONTENT_SETTLED: 'DUPLICATE_CONTENT_SETTLED',
  END_LOADING_LABEL_SETS: 'END_LOADING_LABEL_SETS',
  MOVE_CONTENT: 'MOVE_CONTENT',
  SET_DRAG_ORIGIN: 'SET_DRAG_ORIGIN',
  REMOVE_MOMENT_UPDATE: 'REMOVE_MOMENT_UPDATE',
  REORDER_CONTENT: 'REORDER_CONTENT',
  START_MOMENT_FETCH: 'START_MOMENT_FETCH',
  END_MOMENT_FETCH: 'END_MOMENT_FETCH',
  UPDATE_MOMENT: 'UPDATE_MOMENT',
}

const bootstrapWeekView = (state, payload) => {
  return {
    ...state,
    moments: payload.moments || state.moments,
    momentUpdates: [],
  }
}

export const reducer = (state, action) => {
  const payload = action.data

  switch (action.type) {
    case actionTypes.ACTIVATE_MODAL:
      return {
        ...state,
        activeEntityInModal: {
          ...payload.params,
          type: payload.entityType,
        },
        activeModalKey: payload.activeModalKey,
        isMomentCreatorActive: false,
      }
    case actionTypes.BOOTSTRAP_WEEK_VIEW:
      return bootstrapWeekView(state, payload)
    case actionTypes.CHANGE_WEEK:
      return {
        ...state,
        startDate: startOfWeek(payload.focusedDay, {
          weekStartsOn: payload.weekStartsOn,
        }),
      }
    case actionTypes.DEACTIVATE_MODAL:
      return {
        ...state,
        activeEntityInModal: null,
      }
    case actionTypes.DELETE_CONTENT:
      return {
        ...state,
        activeEntityInModal: null,
        moments: {
          ...state.moments,
          [payload.momentId]: {
            ...state.moments[payload.momentId],
            ...payload.update,
          },
        },
      }
    case actionTypes.DELETE_MOMENT: {
      const moments = { ...state.moments }
      delete moments[payload.momentId]
      return {
        ...state,
        activeEntityInModal: null,
        moments: {
          ...moments,
        },
      }
    }
    case actionTypes.DUPLICATE_CONTENT: {
      // The `{ id: null, isSaving: true, uuid }` shape below is also read by
      // Content mode's `collectOptimisticPosts` in `useMomentMap/contentMap.tsx`,
      // which surfaces the same placeholder as a synthetic content entry. If
      // this shape changes, update the reader there too.
      const newContent = {
        ...payload.contentItem,
        name:
          payload.contentItem.name.indexOf('(copy)') === -1
            ? `${payload.contentItem.name} (copy)`
            : payload.contentItem.name,
        id: null,
        is_published: false,
        isSaving: true,
        uuid: uuidv4(),
      }
      const updatedContentList = addContentToList(
        payload.contentList.findIndex((c) => c.id === payload.contentItem.id) +
          1,
        newContent,
        payload.moment.posts
      )
      const destinationMoment = buildDestinationMoment(
        updatedContentList,
        payload.moment
      )
      return {
        ...state,
        destinationMoment: state.moments[payload.moment.id],
        destinationMomentPivotName: payload.pivotName,
        duplicatedContentSource: payload.contentItem,
        optimisticDuplicateUuid: newContent.uuid,
        moments: {
          ...state.moments,
          [payload.moment.id]: destinationMoment,
        },
        originMoment: state.moments[payload.moment.id],
        originMomentPivotName: payload.pivotName,
      }
    }
    case actionTypes.DUPLICATE_CONTENT_SETTLED: {
      // Fired after the duplicate mutation settles (success OR failure),
      // including from `!serviceId` bail paths. Removes the specific
      // optimistic post identified by `payload.optimisticUuid` so we
      // don't accidentally nuke an unrelated in-flight duplicate when
      // two are racing. Safe to dispatch multiple times.
      const { momentId, optimisticUuid } = payload
      const moment = state.moments?.[momentId]
      const baseStateUpdate = {
        ...state,
        duplicatedContentSource:
          state.optimisticDuplicateUuid === optimisticUuid
            ? null
            : state.duplicatedContentSource,
        optimisticDuplicateUuid:
          state.optimisticDuplicateUuid === optimisticUuid
            ? null
            : state.optimisticDuplicateUuid,
      }
      if (!moment) return baseStateUpdate
      const posts = (moment.posts || []).filter(
        (post) => post.uuid !== optimisticUuid
      )
      return {
        ...baseStateUpdate,
        moments: {
          ...state.moments,
          [momentId]: { ...moment, posts, isUpdatingContent: false },
        },
      }
    }
    case actionTypes.END_LOADING_LABEL_SETS:
      return {
        ...state,
        isLoadingLabelSets: false,
      }
    case actionTypes.END_MOMENT_FETCH:
      // A week left behind can finish loading after the next one has begun,
      // and its moments are not this week's.
      if (payload?.startDate && !isEqual(payload.startDate, state.startDate))
        return state
      return {
        ...state,
        isLoadingMoments: false,
        momentsLoadedFor: payload ?? null,
      }
    case actionTypes.MOVE_CONTENT: {
      const {
        originMomentId,
        originMomentContent,
        destinationMomentId,
        destinationMomentContent,
        movedContent,
        newContentPosition,
      } = payload
      const contentListWithoutMovedContent = originMomentContent?.filter(
        (content) => content.id !== movedContent.id
      )
      const originMoment = buildOriginMoment(
        contentListWithoutMovedContent,
        state.moments[originMomentId]
      )

      const destinationMoment = buildDestinationMoment(
        addContentToList(
          newContentPosition,
          movedContent,
          destinationMomentContent
        ),
        state.moments[destinationMomentId]
      )
      return {
        ...state,
        moments: {
          ...state.moments,
          [originMomentId]: originMoment,
          [destinationMomentId]: destinationMoment,
        },
      }
    }
    case actionTypes.REORDER_CONTENT: {
      const updatedContentList = reorderContentInList(
        payload.moment.posts,
        payload.originIndex,
        payload.destinationIndex
      )
      return {
        ...state,
        moments: {
          ...state.moments,
          [payload.moment.id]: buildDestinationMoment(
            updatedContentList,
            payload.moment
          ),
        },
      }
    }
    case actionTypes.SET_DRAG_ORIGIN:
      return {
        ...state,
        originMoment: state.moments[payload.momentId],
        originMomentPivotName: payload.originMomentPivotName,
      }
    case actionTypes.START_MOMENT_FETCH:
      return {
        ...state,
        isLoadingMoments: true,
      }
    case actionTypes.REMOVE_MOMENT_UPDATE: {
      const momentUpdates = [...state.momentUpdates]
      momentUpdates.pop()
      return {
        ...state,
        dropData: payload,
        momentUpdates,
      }
    }
    case actionTypes.UPDATE_MOMENT: {
      const momentUpdates = [...state.momentUpdates]
      momentUpdates.push(true)
      return {
        ...state,
        draggedMomentParams: null,
        dropData: payload.dropData,
        moments: {
          ...state.moments,
          [payload.momentId]: {
            ...state.moments[payload.momentId],
            ...payload.update,
            isSaving: true,
          },
        },
        momentUpdates,
      }
    }
    default:
      return state
  }
}
